// ---------------------------------------------------------------------------
// Role types
// ---------------------------------------------------------------------------

export type RoleId = 'super_admin' | 'tenant_admin' | 'reviewer' | 'company_user';

// ---------------------------------------------------------------------------
// Admin -- user management
// ---------------------------------------------------------------------------

export interface AdminUser {
  id: string;
  email: string;
  roles: RoleId[];
  companyId: string | null;
  companyName: string | null;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface PatchUserRolesRequest {
  /** Full desired role list -- replaces the current set */
  roles: RoleId[];
}

// ---------------------------------------------------------------------------
// Admin -- application overview
// ---------------------------------------------------------------------------

export type ApplicationStatus =
  | 'draft'
  | 'submitted'
  | 'preq_under_review'
  | 'preq_passed'
  | 'preq_failed'
  | 'under_review'
  | 'deficiency_required'
  | 'deficiency_submitted'
  | 'completed'
  | 'approved'
  | 'rejected'
  /** Board vetoed a reviewer-approved application */
  | 'board_rejected'
  /** Board overrode a reviewer-rejected application */
  | 'board_approved'
  /** Submission triggered an exclusion flag — awaiting admin review */
  | 'pending_exclusion_review'
  /** Admin rejected the application based on exclusion factors */
  | 'excluded';

export interface ApplicationReviewer {
  id: string;
  email: string;
}

export interface ApplicationSummary {
  id: string;
  year: number;
  type: 'new' | 'renewal';
  status: ApplicationStatus;
  companyId: string;
  companyName: string;
  taxId: string;
  submittedAt: string | null;
  createdAt: string;
  reviewers: ApplicationReviewer[];
  /** @deprecated use reviewers[].id */
  reviewerIds: string[];
  /** System-suggested excellent applicant flag (auto-computed after finalization) */
  excellentSuggested: boolean;
  /** Board's final decision: null = pending, true = approved, false = rejected */
  excellentApproved: boolean | null;
}

export interface AdminApplicationDetail {
  id: string;
  year: number;
  type: 'new' | 'renewal';
  status: ApplicationStatus;
  companyId: string;
  companyName: string;
  taxId: string;
  submittedAt: string | null;
  createdAt: string;
  reviewers: ApplicationReviewer[];
  /** System-suggested excellent applicant flag (auto-computed after finalization) */
  excellentSuggested: boolean;
  /** Board's final decision: null = pending, true = approved, false = rejected */
  excellentApproved: boolean | null;
  prequalification: {
    totalScore: number;
    maxScore: number;
    passed: boolean;
    percentage: number;
    hasEmployees: boolean;
    renewalBypass: boolean;
  } | null;
  /** EAV form data: section -> fieldKey -> value */
  formData: Record<string, Record<string, string | null>>;
  /** Per-reviewer score summaries (finalized + draft) */
  reviewScores: ReviewerScoreSummary[];
}

export interface PatchApplicationStatusRequest {
  status: ApplicationStatus;
}

export interface AssignReviewerRequest {
  reviewerId: string;
}

// ---------------------------------------------------------------------------
// Admin -- form field edit audit log
// ---------------------------------------------------------------------------

/** Statuses where admin may edit application form data */
export const ALL_APPLICATION_STATUSES: ApplicationStatus[] = [
  'draft', 'submitted', 'preq_under_review', 'preq_passed', 'preq_failed',
  'under_review', 'deficiency_required', 'deficiency_submitted',
  'completed', 'approved', 'rejected', 'board_rejected', 'board_approved',
  'pending_exclusion_review', 'excluded',
];

export const ADMIN_EDITABLE_STATUSES: ApplicationStatus[] = ['submitted', 'under_review'];

export interface AdminFormEdit {
  id: string;
  applicationId: string;
  adminId: string;
  adminEmail: string;
  section: string;
  fieldKey: string;
  oldValue: string | null;
  newValue: string | null;
  editedAt: string;
}

export interface AdminFormEditRequest {
  section: string;
  fieldKey: string;
  value: string;
}

// ---------------------------------------------------------------------------
// Super-admin -- tenant management
// ---------------------------------------------------------------------------

export interface TenantSummary {
  id: string;
  code: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  userCount: number;
  applicationCount: number;
}

export interface CreateTenantRequest {
  code: string;
  name: string;
}

export interface PatchTenantRequest {
  name?: string;
  isActive?: boolean;
}

// ---------------------------------------------------------------------------
// Reviewer -- scoring
// ---------------------------------------------------------------------------

export interface ReviewScoreItem {
  criterionId: string;
  score: number | null;
  maxScore: number | null;
  comment: string | null;
  isFinal: boolean;
  updatedAt: string;
}

export interface UpsertScoreRequest {
  score: number;
  maxScore: number;
  comment?: string;
}

export interface ReviewerApplicationSummary {
  id: string;
  year: number;
  type: 'new' | 'renewal';
  status: ApplicationStatus;
  companyId: string;
  companyName: string;
  taxId: string;
  submittedAt: string | null;
  assignedAt: string;
  /** Has this reviewer submitted any final scores yet? */
  hasFinalized: boolean;
}

// ---------------------------------------------------------------------------
// Admin -- prequalification detail (question-level answers)
// ---------------------------------------------------------------------------

export interface PreqAnswerItem {
  questionNumber: number;
  questionText: string;
  selectedOptionText: string | null;
  score: number | null;
  maxScore: number;
  isApplicable: boolean;
}

export interface AdminPrequalificationDetail {
  totalScore: number;
  maxScore: number;
  passed: boolean;
  percentage: number;
  hasEmployees: boolean;
  renewalBypass: boolean;
  answers: PreqAnswerItem[];
  onlineUrls: {
    websiteUrl:          string | null;
    facebookUrl:         string | null;
    instagramUrl:        string | null;
    youtubeUrl:          string | null;
    linkedinUrl:         string | null;
    otherOnlinePresence: string | null;
  };
}

export interface ReviewerPrequalificationDetail extends AdminPrequalificationDetail {
  /** Online presence URLs from the company profile */
  onlineUrls: {
    websiteUrl:           string | null;
    facebookUrl:          string | null;
    instagramUrl:         string | null;
    youtubeUrl:           string | null;
    linkedinUrl:          string | null;
    otherOnlinePresence:  string | null;
  };
}

// ---------------------------------------------------------------------------
// Reviewer -- scoring criteria config
// ---------------------------------------------------------------------------

export interface ReviewSubcriteria {
  id: string;
  subcritId: string;
  label: string;
  maxScore: number;
  sortOrder: number;
}

export interface ReviewCriterion {
  id: string;
  criterionId: string;
  label: string;
  maxScore: number;
  sortOrder: number;
  /** 'standard' = 4 rubric sub-criteria; 'simple' = single 0–max score */
  rubricType: 'standard' | 'simple';
  /** Populated for standard criteria; empty array for simple/bonus */
  subcriteria: ReviewSubcriteria[];
}

/** Payload for PATCH /api/config/admin/criteria/:id */
export interface AdminCriterionUpdateRequest {
  maxScore: number;
  label?: string;
}

/** Payload for PATCH /api/config/admin/subcriteria/:id */
export interface AdminSubcriteriaUpdateRequest {
  maxScore: number;
}

export interface ReviewerScoreSummary {
  reviewerId: string;
  reviewerEmail: string;
  totalScore: number;
  maxScore: number;
  finalized: boolean;
  scoredCount: number;
}

// ---------------------------------------------------------------------------
// Deficiency requests (hianypotlas)
// ---------------------------------------------------------------------------

export interface DeficiencyRequest {
  id: string;
  applicationId: string;
  createdById: string;
  createdByEmail: string;
  description: string;
  deadline: string | null;
  resolvedAt: string | null;
  createdAt: string;
  /** Free-text response submitted by the applicant */
  applicantResponse: string | null;
  /** Snapshot of application_form_data at the time the request was created */
  formSnapshot: Record<string, Record<string, string | null>> | null;
  /** Per-section notes written by the reviewer (shown to applicant) */
  sectionNotes: Record<string, string> | null;
  /** Per-section responses written by the applicant */
  sectionResponses: Record<string, string> | null;
}

export interface DeficiencyCreateRequest {
  description: string;
  deadline?: string;
  /** Per-section notes to include with this deficiency request */
  sectionNotes?: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Exclusion factor review
// ---------------------------------------------------------------------------

export interface FlaggedDeclaration {
  declarationKey: string;
  label: string;
}

export interface ExclusionReview {
  id: string;
  applicationId: string;
  flaggedDeclarations: FlaggedDeclaration[];
  decision: 'accepted' | 'rejected' | 'clarification_needed' | null;
  reason: string | null;
  decidedByEmail: string | null;
  decidedAt: string | null;
  createdAt: string;
}

export interface ExclusionDecideRequest {
  decision: 'accepted' | 'rejected' | 'clarification_needed';
  reason: string;
}

// ---------------------------------------------------------------------------
// Email log
// ---------------------------------------------------------------------------

export interface EmailLogEntry {
  id: string;
  applicationId: string | null;
  companyName: string | null;
  event: string;
  recipientEmail: string;
  status: 'pending' | 'sent' | 'failed';
  attempts: number;
  lastError: string | null;
  sentAt: string | null;
  createdAt: string;
}

// ---------------------------------------------------------------------------
// Email templates
// ---------------------------------------------------------------------------

export interface EmailTemplate {
  id: string | null;
  tenantId: string;
  event: string;
  /** Human-readable label returned by the API (from EVENT_LABELS on the server) */
  label: string;
  /** Who receives this email — e.g. 'Pályázó', 'Bíráló', 'Meghívott' */
  recipient: string | null;
  subject: string;
  htmlBody: string;
  updatedAt: string | null;
  updatedByEmail: string | null;
  /** True when the row comes from DB (tenant override), false when it is the default */
  isCustom: boolean;
}

export interface EmailTemplateUpdateRequest {
  subject: string;
  htmlBody: string;
}

/** Variable names available per event (for UI hints) */
export const EMAIL_TEMPLATE_VARIABLES: Record<string, string[]> = {
  application_submitted:         ['companyName', 'appLink'],
  application_submitted_admin:   ['companyName', 'appLink', 'taxId'],
  preq_passed:                   ['companyName', 'appLink', 'totalScore', 'maxScore', 'percentage'],
  preq_failed:                   ['companyName', 'appLink', 'totalScore', 'maxScore', 'percentage', 'threshold'],
  under_review:                  ['companyName', 'appLink'],
  deficiency_required:           ['companyName', 'appLink', 'description', 'deadline'],
  deficiency_submitted:          ['companyName', 'appLink'],
  approved:                      ['companyName', 'appLink'],
  rejected:                      ['companyName', 'appLink'],
  board_rejected:                ['companyName', 'appLink'],
  board_approved:                ['companyName', 'appLink'],
  reviewer_assigned:             ['companyName', 'appLink'],
  exclusion_review_required:     ['companyName', 'adminLink', 'flaggedDeclarations'],
  exclusion_accepted:            ['companyName', 'appLink', 'reason'],
  exclusion_rejected:            ['companyName', 'appLink', 'reason'],
  exclusion_clarification_needed:['companyName', 'appLink', 'reason'],
};


// ---------------------------------------------------------------------------
// Compliance checks
// ---------------------------------------------------------------------------
export type ComplianceStatus = 'clean' | 'dirty' | 'error';

export interface ComplianceCheck {
  id:           string;
  ranAt:        string;
  ranByUserId:  string | null;
  navStatus:    ComplianceStatus;
  navDetail:    string | null;
  rmkStatus:    ComplianceStatus;
  rmkDetail:    string | null;
  nrmkStatus:   ComplianceStatus;
  nrmkDetail:   string | null;
}

export interface ComplianceCheckResponse {
  check: ComplianceCheck | null;
}
